import type { FormInstance, FormListFieldData } from "antd";
import { Alert, Empty, Form, Tag } from "antd";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import SchemaSurvivorshipRuleFieldProperty from "components/atoms/SchemaSurvivorshipRuleFieldProperty";
import AttachmentFieldProperties from "components/molecules/AttachmentFieldProperty";
import BooleanFieldProperties from "components/molecules/BooleanFieldProperties";
import CurrencyFieldProperties from "components/molecules/CurrencyFieldProperties";
import DateDayFieldProperties from "components/molecules/DateDayFieldProperties";
import DateFieldProperties from "components/molecules/DateFieldProperties";
import DateMonthFieldProperties from "components/molecules/DateMonthFieldProperties";
import DateTimeFieldProperties from "components/molecules/DateTimeFieldProperties";
import DateYearFieldProperties from "components/molecules/DateYearFieldProperties";
import EmailFieldProperties from "components/molecules/EmailFieldProperties";
import FloatFieldProperties from "components/molecules/FloatFieldProperties";
import IntegerFieldProperties from "components/molecules/IntegerFieldProperties";
import LatLongFieldProperties from "components/molecules/LatLongFieldProperties";
import { LatitudeFieldProperties, LongitudeFieldProperties } from "components/molecules/LontiudeandLatitudeFieldproperties";
import PhoneFieldProperties from "components/molecules/PhoneFieldProperties";
import StringFieldProperties from "components/molecules/StringFieldProperties";
import TimeFieldProperties from "components/molecules/TimeFieldProperties";
import UrlFieldProperties from "components/molecules/UrlFieldProperties";
import SchemaPicklistFieldProperties from "containers/SchemaPicklistFieldProperties";

interface FieldPropertiesViewProps {
    dataType: string;
    formRef: React.RefObject<FormInstance>;
    selectedFieldRow: FormListFieldData;
    readOnly: boolean;
    getFormErrors: (name: string) => Object;
    getBusinessAreaDataForBusinessAreaResult?: IResultState;
    businessAreaDataList?: IBusinessAreaData[];
}

const FieldPropertiesView: React.FC<FieldPropertiesViewProps> = (props) => {
    switch (props.dataType) {
        case "integer":
            return <IntegerFieldProperties {...props} />;
        case "float":
            return <FloatFieldProperties {...props} />;
        case "string":
            return <StringFieldProperties {...props} />;
        case "boolean":
            return <BooleanFieldProperties {...props} />;
        case "datetime":
            return <DateTimeFieldProperties {...props} />;
        case "date":
            return <DateFieldProperties {...props} />;
        case "time":
            return <TimeFieldProperties {...props} />;
        case "date-year":
            return <DateYearFieldProperties {...props} />;
        case "date-month":
            return <DateMonthFieldProperties {...props} />;
        case "date-day":
            return <DateDayFieldProperties {...props} />;
        case "currency":
            return <CurrencyFieldProperties {...props} />;
        case "picklist":
            return <SchemaPicklistFieldProperties {...props} />;
        case "email":
            return <EmailFieldProperties {...props} />;
        case "url":
            return <UrlFieldProperties {...props} />;
        case "phone":
            return <PhoneFieldProperties {...props} />;
        case "latlng":
            return <LatLongFieldProperties {...props} />;
        case "latitude":
            return <LatitudeFieldProperties {...props} />;
        case "longitude":
            return <LongitudeFieldProperties {...props} />;
        case "attachment":
            return <AttachmentFieldProperties {...props} />;
        case "image":
            return <AttachmentFieldProperties {...props} />;
        case "document":
            return <AttachmentFieldProperties {...props} />;
        default:
            break;
    }
    return <StringFieldProperties {...props} />;
};

interface SchemaModelFieldPropertiesProps {
    formRef: React.RefObject<FormInstance>;
    selectedFieldRow: FormListFieldData | null;
    fieldDataType: string;
    readOnly?: boolean;
    getFormErrors?: (name: string) => Object;
    getBusinessAreaDataForBusinessAreaResult?: IResultState;
    businessAreaDataList?: IBusinessAreaData[];
}

const SchemaModelFieldProperties: React.FC<SchemaModelFieldPropertiesProps> = ({
    formRef,
    selectedFieldRow,
    fieldDataType,
    readOnly,
    getFormErrors,
    getBusinessAreaDataForBusinessAreaResult,
    businessAreaDataList,
}) => {
    if (!selectedFieldRow) {
        return (
            <div className="form-container flex size-full flex-1 flex-col items-center justify-center">
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No schema field selected" />
            </div>
        );
    }

    const fieldError = (getFormErrors || (() => ({})))(`fields.${selectedFieldRow.name}.`) as { help?: React.ReactNode };

    return (
        <FlexContainerLayout
            containerClassName="form-container"
            contentClassName="gap-2"
            header={
                <Form.Item shouldUpdate={true} className="segment-group-header-item w-full min-w-0">
                    {() => {
                        const description = formRef.current?.getFieldValue(["fields", selectedFieldRow.name, "description"]) || "<<Empty>>";
                        const name = formRef.current?.getFieldValue(["fields", selectedFieldRow.name, "name"]) || "<<Empty>>";

                        return (
                            <h3 className="flex w-full min-w-0 items-center gap-2 text-xl font-bold">
                                <span className="truncate" title={description}>
                                    {description}
                                </span>
                                <Tag className="!mb-0 max-w-[40%] shrink-0 truncate" title={name}>
                                    {name}
                                </Tag>
                            </h3>
                        );
                    }}
                </Form.Item>
            }
        >
            {fieldError.help && <Alert type="error" description={fieldError.help} className="mb-2" />}
            <FieldPropertiesView
                formRef={formRef}
                selectedFieldRow={selectedFieldRow}
                readOnly={readOnly || false}
                dataType={fieldDataType}
                getFormErrors={getFormErrors || (() => ({}))}
                getBusinessAreaDataForBusinessAreaResult={getBusinessAreaDataForBusinessAreaResult}
                businessAreaDataList={businessAreaDataList}
            />
            <SchemaSurvivorshipRuleFieldProperty formRef={formRef} selectedFieldRow={selectedFieldRow} readOnly={readOnly || false} />
        </FlexContainerLayout>
    );
};

export default SchemaModelFieldProperties;
